<template>
  <div class="billing-portal">
    <div class="billing-portal-container">
      <!-- Header -->
      <div class="billing-portal-header">
        <h1 class="billing-portal-title">Billing Portal</h1>
        <p class="billing-portal-subtitle">
          Manage your subscription, payment methods, and billing information
        </p>
      </div>
      
      <!-- Tabs -->
      <div class="billing-portal-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.name"
          @click="currentTab = tab.name"
          :class="[
            'billing-portal-tab',
            { 'active': currentTab === tab.name }
          ]"
        >
          {{ tab.label }}
          <themed-badge v-if="tab.count" variant="secondary" class="ml-2">
            {{ tab.count }}
          </themed-badge>
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="billing-portal-loading">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="billing-portal-error">
        <div class="flex">
          <div class="flex-shrink-0">
            <theme-icon icon="alert-circle" type="icons" />
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium">Error</h3>
            <div class="mt-2 text-sm">{{ error }}</div>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div v-else class="billing-portal-content">
        <!-- Subscription Tab -->
        <subscription-management
          v-if="currentTab === 'subscription'"
          @error="handleError"
        />

        <!-- Payment Methods Tab -->
        <payment-methods v-else-if="currentTab === 'payment-methods'" />
        
        <!-- Billing Info Tab -->
        <billing-information v-else-if="currentTab === 'billing-info'" />
        
        <!-- Invoices Tab -->
        <invoice-history v-else-if="currentTab === 'invoices'" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import ThemedBadge from '../ThemedBadge.vue';
import ThemeIcon from '../ThemeIcon.vue';
import PaymentMethods from './PaymentMethods.vue';
import BillingInformation from './BillingInformation.vue';
import InvoiceHistory from './InvoiceHistory.vue';
import SubscriptionManagement from './SubscriptionManagement.vue';

export default {
  name: 'BillingPortal',
  components: {
    ThemedBadge,
    ThemeIcon,
    PaymentMethods,
    BillingInformation,
    InvoiceHistory,
    SubscriptionManagement
  },
  props: {
    type: {
      type: String,
      default: 'subscription'
    }
  },
  data() {
    return {
      currentTab: '',
      loading: false,
      error: null,
      stripeKey: window.stripeKey || '',
      tabs: [
        { name: 'subscription', label: 'Subscription' },
        { name: 'payment-methods', label: 'Payment Methods', count: 0 },
        { name: 'billing-info', label: 'Billing Information' },
        { name: 'invoices', label: 'Invoice History', count: 0 }
      ]
    };
  },
  computed: {
    ...mapState('billing', [
      'paymentMethods',
      'invoices',
      'billingInfo',
    ])
  },
  watch: {
    type: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.tabs.some(tab => tab.name === newVal)) {
          this.currentTab = newVal;
        } else {
          this.currentTab = 'subscription';
        }
      }
    },
    currentTab(newTab) {
      window.location.hash = newTab;
    }
  },
  async mounted() {
    try {
      this.loading = true;
      await Promise.all([
        this.fetchPaymentMethods(),
        this.fetchInvoices()
      ]);
      
      // Set tab counts
      this.tabs = this.tabs.map(tab => {
        if (tab.name === 'payment-methods') {
          return { ...tab, count: this.paymentMethods?.length || 0 };
        } else if (tab.name === 'invoices') {
          return { ...tab, count: this.invoices?.items?.length || 0 };
        }
        return tab;
      });
    } catch (error) {
      console.error('Error initializing billing portal:', error);
      this.error = 'Failed to load billing information. Please try again later.';
    } finally {
      this.loading = false;
    }
  },
  methods: {
    ...mapActions('billing', [
      'fetchPaymentMethods',
      'fetchInvoices',
      'fetchBillingInfo',
    ]),
    handleError(error) {
      this.error = error;
      console.error('Billing error:', error);
    },
  }
};
</script>
